import { get } from 'svelte/store';

import { real_id } from 'src/helpers/interface_helpers';
import { getRequiredWaveParams, hasWaveConfigurationValue } from 'src/helpers/z_wave_settings_config';
import { zwaveCall } from 'src/services/xz_http_reqs';
import { devices_settings, devices_status } from 'src/stores';
import type { Device } from 'src/types/Device';

const WAVE_CONFIG_GET_CONCURRENCY = 1;
const WAVE_CONFIG_ABORT_AFTER_CONSECUTIVE_FAILURES = 6;

const logWaveConfig = (stage: string, data: Record<string, unknown> = {}) => {
	console.log(`[zwave][config] ${stage}`, data);
};

const isDeviceErrorResponse = (response: unknown): boolean => {
	if (!response || typeof response !== 'object') {
		return false;
	}
	const payload = response as Record<string, unknown>;
	const error = (payload.error || payload.data) as Record<string, unknown> | undefined;
	if (error && typeof error === 'object' && error.code === 'DEVICE_ERROR_RESPONSE') {
		return true;
	}
	return false;
};

/**
 * Extract a Configuration.Get numeric value from an XZ / local RPC response.
 * Existing callers (e.g. AutoZ) treat `res.data` as the bare number.
 */
export const parseConfigurationGetValue = (response: unknown): number | undefined => {
	if (response === null || typeof response === 'undefined') {
		return undefined;
	}

	if (typeof response === 'number' && Number.isFinite(response)) {
		return response;
	}

	if (typeof response === 'string' && response.trim() !== '' && !Number.isNaN(+response)) {
		return +response;
	}

	if (typeof response !== 'object') {
		return undefined;
	}

	const payload = response as Record<string, unknown>;
	const candidates: unknown[] = [payload.data, payload.result, payload.value, payload];

	for (const candidate of candidates) {
		if (typeof candidate === 'number' && Number.isFinite(candidate)) {
			return candidate;
		}
		if (typeof candidate === 'string' && candidate.trim() !== '' && !Number.isNaN(+candidate)) {
			return +candidate;
		}
		if (!candidate || typeof candidate !== 'object') {
			continue;
		}
		const record = candidate as Record<string, unknown>;
		if (typeof record.value === 'number' && Number.isFinite(record.value)) {
			return record.value;
		}
		if (
			typeof record.value === 'string' &&
			record.value.trim() !== '' &&
			!Number.isNaN(+record.value)
		) {
			return +record.value;
		}
		if (typeof record.parameterValue === 'number' && Number.isFinite(record.parameterValue)) {
			return record.parameterValue;
		}
	}

	return undefined;
};

/**
 * Merge one Configuration CC param into devices_settings[id]['Configuration:0'].
 */
export const mergeWaveConfigurationParam = (
	deviceId: string,
	param: string | number,
	value: number,
): void => {
	const id = real_id(deviceId);
	const existing = get(devices_settings)?.[id] || {};
	const configuration = {
		...(existing['Configuration:0'] || {}),
		[String(param)]: value,
	};

	devices_settings.update_devices_settings(id, {
		...existing,
		'Configuration:0': configuration,
	});
};

export const fetchWaveConfigurationParam = async (
	device: Device,
	param: string,
): Promise<{ param: string; value?: number; ok: boolean; deviceError?: boolean }> => {
	try {
		const response = await zwaveCall(device, {
			cc: 'Configuration',
			m: 'get',
			ep: 0,
			params: [Number(param)],
		});

		if (!response?.isok) {
			return {
				param,
				ok: false,
				deviceError: isDeviceErrorResponse(response) || Boolean(response?.error),
			};
		}

		const value = parseConfigurationGetValue(response);
		if (typeof value === 'undefined') {
			return { param, ok: false, deviceError: false };
		}

		mergeWaveConfigurationParam(device.id, param, value);
		return { param, value, ok: true };
	} catch {
		return { param, ok: false, deviceError: true };
	}
};

const runWithConcurrency = async <T>(
	items: T[],
	concurrency: number,
	worker: (item: T) => Promise<'continue' | 'abort'>,
): Promise<'completed' | 'aborted'> => {
	if (items.length === 0) {
		return 'completed';
	}

	let nextIndex = 0;
	let aborted = false;

	const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
		while (!aborted && nextIndex < items.length) {
			const currentIndex = nextIndex;
			nextIndex += 1;
			const result = await worker(items[currentIndex]);
			if (result === 'abort') {
				aborted = true;
				return;
			}
		}
	});

	await Promise.all(runners);
	return aborted ? 'aborted' : 'completed';
};

/**
 * Configuration.Get for curated required params missing from Configuration:0.
 * Sequential, soft-fail, early abort on repeated DEVICE_ERROR_RESPONSE.
 */
export const ensureWaveConfigurationSettings = async (
	device: Device,
): Promise<Record<string, number>> => {
	const id = real_id(device.id);
	const params = getRequiredWaveParams(device.type);
	const existing = get(devices_settings)?.[id]?.['Configuration:0'] || {};

	if (params.length === 0) {
		return { ...existing };
	}

	const status = devices_status.getByRealId(id);
	if (status?.online === false) {
		logWaveConfig('skip_offline', { deviceId: id, type: device.type });
		return { ...existing };
	}

	const gaps = params.filter((param) => !hasWaveConfigurationValue(existing, param));
	const skippedCached = params.length - gaps.length;

	logWaveConfig('hydrate_start', {
		deviceId: id,
		type: device.type,
		requested: params.length,
		skippedCached,
		gaps,
	});

	if (gaps.length === 0) {
		logWaveConfig('hydrate_done', {
			deviceId: id,
			requested: params.length,
			skippedCached,
			successCount: 0,
			failedCount: 0,
			aborted: false,
		});
		return { ...existing };
	}

	let successCount = 0;
	let failedCount = 0;
	let consecutiveFailures = 0;

	const outcome = await runWithConcurrency(gaps, WAVE_CONFIG_GET_CONCURRENCY, async (param) => {
		const result = await fetchWaveConfigurationParam(device, param);
		if (result.ok) {
			successCount += 1;
			consecutiveFailures = 0;
			return 'continue';
		}

		failedCount += 1;
		consecutiveFailures += 1;
		if (result.deviceError && consecutiveFailures >= WAVE_CONFIG_ABORT_AFTER_CONSECUTIVE_FAILURES) {
			return 'abort';
		}
		return 'continue';
	});

	const updated = get(devices_settings)?.[id]?.['Configuration:0'] || {};
	if (outcome === 'aborted') {
		logWaveConfig('hydrate_aborted', {
			deviceId: id,
			requested: params.length,
			skippedCached,
			successCount,
			failedCount,
		});
	} else {
		logWaveConfig('hydrate_done', {
			deviceId: id,
			requested: params.length,
			skippedCached,
			successCount,
			failedCount,
			aborted: false,
		});
	}

	return { ...updated };
};
